import { Button, DialogContent, DialogTitle, FormHelperText, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { DwhShare } from "../../../api/types/integrationsTypes";

interface Props {
  share: DwhShare | undefined;
  onCancel: () => void;
  onShareDeleted: (accountCode: string) => void;
}

const deleteDwhShare = withErrorHandling(adminApi.deleteDwhShare);

const DeleteShareDialog = ({ share, onCancel, onShareDeleted }: Props) => {
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleCancel = () => {
    onCancel();
    setErrorMessage("");
  };

  const handleDelete = async () => {
    if (!share) {
      return;
    }

    setDeleting(true);
    const [, error] = await deleteDwhShare(share.id);
    setDeleting(false);
    if (error) {
      logError(error, "[DeleteShareDialog] deleteDwhShare");
      setErrorMessage(error.message);
      return;
    }

    onShareDeleted(share.accountCode.toUpperCase());
  };

  return (
    <SmallDialog open={!!share} onClose={handleCancel}>
      <DialogTitle>Delete Sharing</DialogTitle>
      <DialogCloseButton onClick={handleCancel} />
      <DialogContent sx={(theme) => ({ px: theme.spacing(4) })}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Are you sure you want to delete the sharing?</Typography>
            <Typography color="textSecondary">{share?.accountCode}</Typography>
          </Stack>
          {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleCancel}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteShareDialog;
